import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import PrelitXLogo from '@/components/PrelitXLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid reset link. Please request a new password reset.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success('Password reset successfully!');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reset password. The link may be invalid or expired.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (error && !token) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-2xl border-0 glass-card">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="mx-auto mb-2" style={{ marginTop: '24px' }}>
              <PrelitXLogo size="large" className="justify-center" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold" style={{ color: '#245451' }}>
                Invalid Reset Link
              </CardTitle>
              <CardDescription className="mt-2" style={{ color: '#64748B' }}>
                {error}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Link to="/forgot-password">
              <Button
                className="w-full h-11 font-medium"
                style={{ backgroundColor: '#245451' }}
              >
                Request New Reset Link
              </Button>
            </Link>
            <div className="mt-4 text-center">
              <Link 
                to="/login" 
                className="text-sm font-medium"
                style={{ color: '#406962' }}
              >
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page-container min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-2xl border-0 glass-card">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="mx-auto mb-2" style={{ marginTop: '24px' }}>
              <PrelitXLogo size="large" className="justify-center" />
            </div>
            <div>
              <CheckCircle className="mx-auto h-16 w-16 mb-4" style={{ color: '#245451' }} />
              <CardTitle className="text-2xl font-bold" style={{ color: '#245451' }}>
                Password Reset Successfully
              </CardTitle>
              <CardDescription className="mt-2" style={{ color: '#64748B' }}>
                Your password has been updated. Redirecting to login...
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button
                className="w-full h-11 font-medium"
                style={{ backgroundColor: '#245451' }}
              >
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-2xl border-0 glass-card">
        <CardHeader className="text-center space-y-4 pb-8">
          <div className="mx-auto mb-2" style={{ marginTop: '24px' }}>
            <PrelitXLogo size="large" className="justify-center" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold" style={{ color: '#245451' }}>
              Set New Password
            </CardTitle>
            <CardDescription className="mt-2" style={{ color: '#64748B' }}>
              Enter your new password below
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="newPassword" style={{ color: '#1A1A1A', fontWeight: 500 }}>
                New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="new-password-input"
              />
              <p className="text-xs" style={{ color: '#64748B' }}>
                Must be at least 8 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" style={{ color: '#1A1A1A', fontWeight: 500 }}>
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="confirm-password-input"
              />
            </div>
            
            {error && (
              <div 
                className="p-3 rounded-lg text-sm"
                style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 font-medium"
              style={{ backgroundColor: '#245451' }}
              disabled={loading}
              data-testid="reset-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm font-medium"
              style={{ color: '#406962' }}
            >
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
